// src/hooks/use2FA.js
import { useState, useCallback } from 'react';
import api from '../services/api';
import { useToast } from '../context/ToastContext';

export function use2FA() {
    const { showToast } = useToast();
    const [qrCodeUrl, setQrCodeUrl] = useState('');
    const [secret, setSecret] = useState('');
    const [token6Digitos, setToken6Digitos] = useState('');
    const [procesando2FA, setProcesando2FA] = useState(false);
    const [mostrarModalVerificacion, setMostrarModalVerificacion] = useState(false);

    // Fase 1: Solicitar sincronización al servidor (Genera clave secreta y QR)
    const iniciarConfiguracion2FA = useCallback(async () => {
        setProcesando2FA(true);
        try {
            // 🛡️ FIX: la ruta real es /auth/2fa/generate (antes /setup → 404) y el
            // backend devuelve { qrCodeUrl, secret }, no 'qrCode'.
            const res = await api.post('/auth/2fa/generate');
            setQrCodeUrl(res?.qrCodeUrl || res?.data?.qrCodeUrl || '');
            setSecret(res?.secret || res?.data?.secret || '');
            setMostrarModalVerificacion(true);
        } catch (error) {
            showToast(error.message || 'No se pudo inicializar el protocolo de seguridad 2FA', 'error');
        } finally {
            setProcesando2FA(false);
        }
    }, [showToast]);

    // Fase 2: Validar el código de 6 dígitos introducido por el médico
    const verificarYActivar2FA = useCallback(async (onSuccess) => {
        if (token6Digitos.length !== 6) {
            showToast('El código de verificación debe constar de exactamente 6 dígitos', 'warning');
            return;
        }

        setProcesando2FA(true);
        try {
            await api.post('/auth/2fa/verify', { token: token6Digitos });
            showToast('Escudo de seguridad Autenticación en Dos Pasos (2FA) activado correctamente.', 'success');
            setMostrarModalVerificacion(false);
            if (onSuccess) onSuccess(); // Recarga los datos del usuario global
        } catch (error) {
            showToast(error.message || 'Código inválido o expirado. Verifique su app de autenticación.', 'error');
        } finally {
            setProcesando2FA(false);
        }
    }, [token6Digitos, showToast]);

    // 🛡️ FIX: el backend exige confirmar la contraseña para desactivar el 2FA
    // (antes se enviaba el POST vacío y siempre respondía 400).
    const desactivar2FA = useCallback(async (password, onSuccess) => {
        if (!password) {
            showToast('Introduce tu contraseña para desactivar el 2FA.', 'warning');
            return false;
        }
        setProcesando2FA(true);
        try {
            await api.post('/auth/2fa/disable', { password });
            showToast('Seguridad degradada: Doble factor desactivado.', 'info');
            if (onSuccess) onSuccess();
            return true;
        } catch (error) {
            showToast(error.message || 'Error al procesar la baja del protocolo 2FA', 'error');
            return false;
        } finally {
            setProcesando2FA(false);
        }
    }, [showToast]);

    return {
        qrCodeUrl,
        secret,
        token6Digitos,
        setToken6Digitos,
        procesando2FA,
        mostrarModalVerificacion,
        setMostrarModalVerificacion,
        iniciarConfiguracion2FA,
        verificarYActivar2FA,
        desactivar2FA
    };
}